import { apiClient } from './api'
import {
  Document,
  DocumentTemplate,
  DocumentUpload,
  DocumentReview,
  UserDocumentStatus,
  DocumentStats,
  BulkDocumentAction,
  KYCApproval,
} from '@/types'

export class DocumentService {
  // Document CRUD operations
  static async getDocuments(): Promise<Document[]> {
    return apiClient.get<Document[]>('/documents/')
  }

  static async getDocument(id: number): Promise<Document> {
    return apiClient.get<Document>(`/documents/${id}/`)
  }

  static async uploadDocument(data: DocumentUpload): Promise<Document> {
    const formData = new FormData()
    formData.append('document_type', data.document_type)
    formData.append('file', data.file)
    formData.append('priority', data.priority)
    if (data.expires_at) {
      formData.append('expires_at', data.expires_at)
    }

    return apiClient.post<Document>('/documents/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  static async updateDocument(id: number, data: DocumentReview): Promise<Document> {
    return apiClient.put<Document>(`/documents/${id}/`, data)
  }

  static async deleteDocument(id: number): Promise<void> {
    return apiClient.delete<void>(`/documents/${id}/`)
  }

  // Document templates
  static async getDocumentTemplates(): Promise<DocumentTemplate[]> {
    return apiClient.get<DocumentTemplate[]>('/documents/templates/')
  }

  // User document status
  static async getUserDocumentStatus(): Promise<UserDocumentStatus> {
    return apiClient.get<UserDocumentStatus>('/documents/status/')
  }

  static async getUserKYCStatus(): Promise<UserDocumentStatus> {
    return apiClient.get<UserDocumentStatus>('/documents/kyc-status/')
  }

  // Admin operations
  static async getDocumentStats(): Promise<DocumentStats> {
    return apiClient.get<DocumentStats>('/documents/admin/stats/')
  }

  static async getPendingDocuments(): Promise<Document[]> {
    return apiClient.get<Document[]>('/documents/admin/pending/')
  }

  static async bulkApproveDocuments(
    data: BulkDocumentAction
  ): Promise<{ message: string; approved_count: number }> {
    return apiClient.post<{ message: string; approved_count: number }>(
      '/documents/admin/bulk-approve/',
      data
    )
  }

  static async bulkRejectDocuments(
    data: BulkDocumentAction
  ): Promise<{ message: string; rejected_count: number }> {
    return apiClient.post<{ message: string; rejected_count: number }>(
      '/documents/admin/bulk-reject/',
      data
    )
  }

  static async approveUserKYC(
    userId: number,
    data: KYCApproval
  ): Promise<{
    message: string
    user_id: number
    kyc_verification_date: string
    kyc_verification_level: string
  }> {
    return apiClient.post<{
      message: string
      user_id: number
      kyc_verification_date: string
      kyc_verification_level: string
    }>(`/documents/admin/approve-kyc/${userId}/`, data)
  }
}

// Document type constants for easy reference
export const DOCUMENT_TYPES = {
  // Identity Documents
  ID_PASSPORT: 'ID_PASSPORT',
  ID_DRIVER_LICENSE: 'ID_DRIVER_LICENSE',
  ID_NATIONAL_ID: 'ID_NATIONAL_ID',
  ID_VOTER_ID: 'ID_VOTER_ID',

  // Address Proof Documents
  ADDRESS_UTILITY_BILL: 'ADDRESS_UTILITY_BILL',
  ADDRESS_BANK_STATEMENT: 'ADDRESS_BANK_STATEMENT',
  ADDRESS_RENTAL_AGREEMENT: 'ADDRESS_RENTAL_AGREEMENT',
  ADDRESS_LEASE_AGREEMENT: 'ADDRESS_LEASE_AGREEMENT',

  // Business Registration Documents
  BUSINESS_CERTIFICATE_INCORPORATION: 'BUSINESS_CERTIFICATE_INCORPORATION',
  BUSINESS_REGISTRATION_CERTIFICATE: 'BUSINESS_REGISTRATION_CERTIFICATE',
  BUSINESS_MOA: 'BUSINESS_MOA',
  BUSINESS_AOA: 'BUSINESS_AOA',
  BUSINESS_PARTNERSHIP_DEED: 'BUSINESS_PARTNERSHIP_DEED',

  // Financial Documents
  FINANCIAL_BANK_STATEMENT: 'FINANCIAL_BANK_STATEMENT',
  FINANCIAL_AUDITED_STATEMENTS: 'FINANCIAL_AUDITED_STATEMENTS',
  FINANCIAL_PROCESSING_STATEMENTS: 'FINANCIAL_PROCESSING_STATEMENTS',
  FINANCIAL_CANCELLED_CHEQUE: 'FINANCIAL_CANCELLED_CHEQUE',

  // Business License Documents
  LICENSE_BUSINESS_LICENSE: 'LICENSE_BUSINESS_LICENSE',
  LICENSE_TRADE_LICENSE: 'LICENSE_TRADE_LICENSE',
  LICENSE_GST_REGISTRATION: 'LICENSE_GST_REGISTRATION',
  LICENSE_TAX_REGISTRATION: 'LICENSE_TAX_REGISTRATION',

  // Authorization Documents
  AUTH_SIGNATORY_DECLARATION: 'AUTH_SIGNATORY_DECLARATION',
  AUTH_BOARD_RESOLUTION: 'AUTH_BOARD_RESOLUTION',
  AUTH_INCUMBENCY_CERTIFICATE: 'AUTH_INCUMBENCY_CERTIFICATE',
  AUTH_GOOD_STANDING_CERTIFICATE: 'AUTH_GOOD_STANDING_CERTIFICATE',

  // Additional Documents
  ADDITIONAL_PROOF_OF_ADDRESS: 'ADDITIONAL_PROOF_OF_ADDRESS',
  ADDITIONAL_OTHER: 'ADDITIONAL_OTHER',
} as const

export const DOCUMENT_TYPE_LABELS = {
  [DOCUMENT_TYPES.ID_PASSPORT]: 'Passport',
  [DOCUMENT_TYPES.ID_DRIVER_LICENSE]: 'Driver License',
  [DOCUMENT_TYPES.ID_NATIONAL_ID]: 'National ID Card',
  [DOCUMENT_TYPES.ID_VOTER_ID]: 'Voter ID Card',
  [DOCUMENT_TYPES.ADDRESS_UTILITY_BILL]: 'Utility Bill',
  [DOCUMENT_TYPES.ADDRESS_BANK_STATEMENT]: 'Bank Statement',
  [DOCUMENT_TYPES.ADDRESS_RENTAL_AGREEMENT]: 'Rental Agreement',
  [DOCUMENT_TYPES.ADDRESS_LEASE_AGREEMENT]: 'Lease Agreement',
  [DOCUMENT_TYPES.BUSINESS_CERTIFICATE_INCORPORATION]: 'Certificate of Incorporation',
  [DOCUMENT_TYPES.BUSINESS_REGISTRATION_CERTIFICATE]: 'Business Registration Certificate',
  [DOCUMENT_TYPES.BUSINESS_MOA]: 'Memorandum of Association',
  [DOCUMENT_TYPES.BUSINESS_AOA]: 'Articles of Association',
  [DOCUMENT_TYPES.BUSINESS_PARTNERSHIP_DEED]: 'Partnership Deed',
  [DOCUMENT_TYPES.FINANCIAL_BANK_STATEMENT]: 'Bank Statement',
  [DOCUMENT_TYPES.FINANCIAL_AUDITED_STATEMENTS]: 'Audited Financial Statements',
  [DOCUMENT_TYPES.FINANCIAL_PROCESSING_STATEMENTS]: 'Payment Processing Statements',
  [DOCUMENT_TYPES.FINANCIAL_CANCELLED_CHEQUE]: 'Cancelled Cheque',
  [DOCUMENT_TYPES.LICENSE_BUSINESS_LICENSE]: 'Business License',
  [DOCUMENT_TYPES.LICENSE_TRADE_LICENSE]: 'Trade License',
  [DOCUMENT_TYPES.LICENSE_GST_REGISTRATION]: 'GST Registration Certificate',
  [DOCUMENT_TYPES.LICENSE_TAX_REGISTRATION]: 'Tax Registration Certificate',
  [DOCUMENT_TYPES.AUTH_SIGNATORY_DECLARATION]: 'Authorized Signatory Declaration',
  [DOCUMENT_TYPES.AUTH_BOARD_RESOLUTION]: 'Board Resolution',
  [DOCUMENT_TYPES.AUTH_INCUMBENCY_CERTIFICATE]: 'Incumbency Certificate',
  [DOCUMENT_TYPES.AUTH_GOOD_STANDING_CERTIFICATE]: 'Good Standing Certificate',
  [DOCUMENT_TYPES.ADDITIONAL_PROOF_OF_ADDRESS]: 'Proof of Business Address',
  [DOCUMENT_TYPES.ADDITIONAL_OTHER]: 'Other Supporting Document',
} as const

export const DOCUMENT_STATUS_LABELS = {
  PENDING: 'Pending Review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
} as const

export const DOCUMENT_PRIORITY_LABELS = {
  REQUIRED: 'Required',
  OPTIONAL: 'Optional',
  RECOMMENDED: 'Recommended',
} as const

export const KYC_STATUS_LABELS = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  PENDING_APPROVAL: 'Pending Approval',
  VERIFIED: 'Verified',
} as const

export const KYC_VERIFICATION_LEVEL_LABELS = {
  BASIC: 'Basic',
  ENHANCED: 'Enhanced',
  FULL: 'Full',
} as const

// Utility functions
export const getDocumentTypeLabel = (type: string): string => {
  return DOCUMENT_TYPE_LABELS[type as keyof typeof DOCUMENT_TYPE_LABELS] || type
}

export const getDocumentStatusLabel = (status: string): string => {
  return DOCUMENT_STATUS_LABELS[status as keyof typeof DOCUMENT_STATUS_LABELS] || status
}

export const getDocumentPriorityLabel = (priority: string): string => {
  return DOCUMENT_PRIORITY_LABELS[priority as keyof typeof DOCUMENT_PRIORITY_LABELS] || priority
}

export const getKYCStatusLabel = (status: string): string => {
  return KYC_STATUS_LABELS[status as keyof typeof KYC_STATUS_LABELS] || status
}

export const getKYCVerificationLevelLabel = (level: string): string => {
  return KYC_VERIFICATION_LEVEL_LABELS[level as keyof typeof KYC_VERIFICATION_LEVEL_LABELS] || level
}

// File validation
export const validateFile = (file: File): { isValid: boolean; error?: string } => {
  const maxSize = 10 * 1024 * 1024 // 10MB
  const allowedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ]

  if (file.size > maxSize) {
    return { isValid: false, error: 'File size cannot exceed 10MB' }
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'File type not supported. Allowed types: PDF, JPG, PNG, DOC, DOCX',
    }
  }

  return { isValid: true }
}

// Format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Format date
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Get status color
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'PENDING':
      return 'text-yellow-600 bg-yellow-100'
    case 'APPROVED':
      return 'text-green-600 bg-green-100'
    case 'REJECTED':
      return 'text-red-600 bg-red-100'
    case 'EXPIRED':
      return 'text-gray-600 bg-gray-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

// Get priority color
export const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'REQUIRED':
      return 'text-red-600 bg-red-100'
    case 'OPTIONAL':
      return 'text-blue-600 bg-blue-100'
    case 'RECOMMENDED':
      return 'text-orange-600 bg-orange-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

// Get KYC status color
export const getKYCStatusColor = (status: string): string => {
  switch (status) {
    case 'VERIFIED':
      return 'text-green-600 bg-green-100'
    case 'PENDING_APPROVAL':
      return 'text-yellow-600 bg-yellow-100'
    case 'IN_PROGRESS':
      return 'text-blue-600 bg-blue-100'
    case 'NOT_STARTED':
      return 'text-gray-600 bg-gray-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}
